<template>
  <div id="companyEdit">
    <div class="container">

      <PageTitle :title="company.name || $t('companies.new')"></PageTitle>

      <Tabs>
        <TabSection :title="$t('branch.general')" collapsible>
          <div class="section-header">
            <div class="subtitle">{{ $t("companies.location_information") }}</div>
            <label>{{ $t("companies.location_information_description") }}</label>
          </div>
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('branch.name') }}</label>
                <input type="text" v-model="company.name" :class="{ invalid: $validate.error('company.name') }">
              </div>
              <div class="group">
                <label>{{ $t('branch.locale.title') }}</label>
                <select type="text" v-model="company.locale" :class="{ invalid: $validate.error('company.locale') }">
                  <option :value="null" selected hidden disabled>{{ $t("branch.locale.select") }}</option>
                  <option :value="locale" v-for="locale of locales" :key="`locale_${locale}`">
                    {{ $t(`translation.${locale}`) }}
                  </option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('branch.country.title') }}</label>
                <select v-model="company.country" :class="{ invalid: $validate.error('company.country') }">
                  <option :value="null" selected hidden disabled>{{ $t("branch.country.select") }}</option>
                  <option v-for="c of countryPresets" :value="c.id" :key="`country_${c.id}`">
                    {{ $t(`country.${c.id}`) }}
                  </option>
                </select>
              </div>
              <div class="group" v-if="company.country && regions.length">
                <label>{{ $t('branch.region.title') }}</label>
                <select v-model="company.region" :class="{ invalid: $validate.error('company.region') }">
                  <option :value="null">{{ $t("branch.region.select") }}</option>
                  <option v-for="r of regions" :value="r.id" :key="`region_${r.id}`">
                    {{ $t(`region.${company.country}.${r.id}`) }}
                  </option>
                </select>
              </div>
              <div class="group" v-if="company.country">
                <label>{{ $t('timezone') }}</label>
                <select v-model="company.timezone" :class="{ invalid: $validate.error('company.timezone') }">
                  <option v-for="timezone of timezones" :value="timezone" :key="`timezone_${timezone}`">{{ timezone }}
                  </option>
                </select>
              </div>
              <div class="group">
                <input id="orderOutsideTimezoneChk" type="checkbox" v-model="company.blockOrdersOutsideTimezone">
                <label for="orderOutsideTimezoneChk">{{ $t("settings.block_orders_outside_timezone") }}</label>
              </div>
              <div class="group" v-if="company.country">
                <label>{{ $t('currency') }}</label>
                <select v-model="company.currency" :class="{ invalid: $validate.error('company.currency') }">
                  <option v-for="currency of currencies" :value="currency" :key="`currency_${currency}`">{{ currency }}
                  </option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('hourFormat') }}</label>
                <select v-model="company.hourFormat">
                  <option :value="f" v-for="f in hourFormats" :key="`format_${f}`">
                    {{ $t(`format.${f}`) }}
                  </option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="group" :class="{ locked: exactlyDistributor }">
                <ImageUpload identifier="companyImageUpdate" @changed="company.image = $event" :object="company.image"
                  :preview-height="215" :maxSizeInMb="0.8"></ImageUpload>
              </div>
              <template v-if="$store.getters.loggedUserHasSuperAdmin">
                <div class="group">
                  <label><span>
                      <tag>Superadmin</tag>&nbsp;&nbsp;Canonical
                    </span></label>
                  <input type="text" class="disabled" readonly :value="canonical">
                </div>
                <div class="group">
                  <label><span>
                      <tag>Superadmin</tag>&nbsp;&nbsp;{{ $t('local_hour') }}
                    </span></label>
                  <input type="text" class="disabled" readonly :value="localHour">
                </div>
              </template>
            </div>
          </div>

          <div v-if="isNew">
            <div class="hr"></div>
            <div class="section-header">
              <div class="subtitle">{{ $t("companies.inventory_selection") }}</div>
              <label>{{ $t("companies.inventory_selection_description") }}</label>
            </div>
            <div class="inventory-init-types">
              <div class="inventory-init-type" v-for="(type, typeIndex) of inventoryInitTypes" :key="'type' + typeIndex"
                   v-if="type !== 'shared' || hasMultipleInventory" @click="company.inventoryInit.strategy = type"
                   :class="{ active: company.inventoryInit.strategy === type }">
                <svgicon class="inventory-init-type__checkbox" :height="16" :width="16"
                         :src="company.inventoryInit.strategy === type ? 'icon/check-circle-full.svg' : 'icon/circle.svg'"></svgicon>
                <div class="inventory-init-type__name">{{ $t("companies.inventory_init." + type + ".title") }}</div>
                <div class="inventory-init-type__description">{{ $t("companies.inventory_init." + type + ".description") }}</div>
              </div>
            </div>

            <template v-if="company.inventoryInit.strategy === 'clone'">
              <div class="group">
                <label>{{ $t("companies.inventory_init.menu_to_copy") }}</label>
                <AjaxSelect
                    v-model="company.inventoryInit.company"
                    :api="`/api/entity/company/select`"
                    :map-fn="restrictCompanySearchToBranch ? (c) => c.name : (c) => `${c.branchName} - ${c.name}`"
                    :query="restrictCompanySearchToBranch ? {branch: branchId} : {}"
                    :allow-empty-search="true"
                    :placeholder="$t('search')"
                />
              </div>
              <div class="group" v-if="$store.getters.loggedUserHasSuperAdmin">
                <input id="chk_restrict_company_search" type="checkbox" v-model="restrictCompanySearchToBranch">
                <label for="chk_restrict_company_search">Restrict search to branch&nbsp;&nbsp;<tag>SuperAdmin</tag></label>
              </div>
            </template>

            <template v-if="company.inventoryInit.strategy === 'share'">
              <div class="group" v-if="company.inventoryInit.strategy === 'share'">
                <label>{{ $t("companies.inventory_init.global_menu_to_use") }}</label>
                <AjaxSelect
                    v-model="company.inventoryInit.inventory"
                    :api="`/api/entity/inventory/select`"
                    :query="{branch: branchId}"
                    :map-fn="(i) => Array.isArray(i.name) ? i.id : `${$to(i.name)} (${i.id})`"
                    :allow-empty-search="true"
                    :placeholder="$t('search')"
                />
              </div>
            </template>

          </div>
        </TabSection>

        <TabSection :title="$t('branch.remoteAccesses.title')" collapsible v-if="canEditRemoteAccesses">
          <div class="access" v-for="(ra, raIndex) in company.remoteAccesses" :key="`ra${raIndex}`">
            <div class="subtitle">
              <div class="flex-grow">#{{ raIndex + 1 }}</div>
              <button class="btn btn-primary" @click="removeRemoteAccess(raIndex)"
                v-if="!exactlyDistributor">{{ $t('branch.remove') }}</button>
            </div>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('branch.remoteAccesses.software') }}</label>
                  <select v-model="ra.software">
                    <option value="anydesk">AnyDesk</option>
                    <option value="teamviewer">TeamViewer</option>
                    <option value="logmein">LogMeIn</option>
                    <option value="other">{{ $t('other') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('branch.remoteAccesses.username') }}</label>
                  <input type="text" v-model="ra.username">
                </div>
                <div class="group">
                  <label>{{ $t('branch.remoteAccesses.password') }}</label>
                  <input type="text" v-model="ra.password">
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('branch.remoteAccesses.accessTo') }}</label>
                  <select v-model="ra.accessTo">
                    <option value="main">{{ $t('branch.remoteAccesses.accessToChoice.main') }}</option>
                    <option value="kiosk">{{ $t('branch.remoteAccesses.accessToChoice.kiosk') }}</option>
                    <option value="menuboard">{{ $t('branch.remoteAccesses.accessToChoice.menuboard') }}</option>
                    <option value="other">{{ $t('other') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('branch.remoteAccesses.notes') }}</label>
                  <textarea v-model="ra.notes"></textarea>
                </div>
              </div>
            </div>
          </div>

          <div v-if="company.remoteAccesses.length === 0">
            <div class="empty">{{ $t("branch.remoteAccesses.empty") }}</div>
          </div>
          <button class="btn btn-primary" @click="addRemoteAccess"
            v-if="!exactlyDistributor">{{ $t('branch.add') }}</button>
        </TabSection>

        <TabSection :title="$t('branch.contact.title')" collapsible>
          <div class="contact" v-for="(contact, contactIndex) in company.contacts" :key="`co${contactIndex}`">
            <div class="subtitle">
              <span class="flex-grow">{{ $t("branch.contact.person") }}</span>
              <Tooltip :text="$t('branch.contact.tooltip')" />
              <button class="btn btn-primary" @click="removeContact(contactIndex)"
                v-if="!exactlyDistributor">{{ $t('branch.remove') }}</button>
            </div>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t("branch.type.title") }}</label>
                  <select v-model="contact.contactType"
                    :class="{ invalid: $validate.error(`company.contacts[${contactIndex}].contactType`) }">
                    <option value="" selected hidden disabled>{{ $t("branch.type.select") }}</option>
                    <option :value="$t('branch.type.distributor')">{{ $t('branch.type.distributor') }}</option>
                    <option :value="$t('branch.type.client')">{{ $t('branch.type.client') }}</option>
                    <option :value="$t('branch.type.billing')">{{ $t('branch.type.billing') }}</option>
                    <option :value="$t('branch.type.notification')">{{ $t('branch.type.notification') }}</option>
                    <option :value="$t('branch.type.other')">{{ $t('branch.type.other') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('branch.first_name') }}</label>
                  <input type="text" v-model="contact.contactFirstName">
                </div>
                <div class="group">
                  <label>{{ $t('branch.name') }}</label>
                  <input type="text" v-model="contact.contactLastName">
                </div>
                <div class="group">
                  <label>{{ $t('branch.company') }}</label>
                  <input type="text" v-model="contact.companyName">
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('branch.email') }}</label>
                  <input type="text" v-model="contact.email"
                    :class="{ invalid: $validate.error(`company.contacts[${contactIndex}].email`) }">
                </div>
                <div class="group">
                  <label>{{ $t('branch.phone') }}</label>
                  <input type="text" v-model="contact.phoneNumber"
                    :class="{ invalid: $validate.error(`company.contacts[${contactIndex}].phoneNumber`) }">
                </div>
                <div class="group">
                  <label>{{ $t('branch.phoneExtension') }}</label>
                  <input type="text" v-model="contact.phoneExtension">
                </div>
                <div class="group">
                  <label>{{ $t('branch.note') }}</label>
                  <textarea v-model="contact.notes" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="company.contacts.length === 0">
            <div class="empty">{{ $t("branch.contact.empty") }}</div>
          </div>
          <button class="btn btn-primary" @click="addContact" v-if="!exactlyDistributor">{{ $t('branch.add') }}</button>
        </TabSection>
      </Tabs>

      <PageFooter :backAction="{action: back}" :mainAction="{ text: $t('save'), action: save }"></PageFooter>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone/index';
  import { mapActions } from 'vuex';
  import ImageUpload from "@/components/tools/ImageUpload.vue";
  import PageTitle from "@/components/PageTitle";
  import AjaxSelect from "@/components/tools/AjaxSelect";
  import PageFooter from "@/components/PageFooter";
  import MevSettings from "@/components/pos/MevSettings";

  export default {

    name: "CompanyEdit",

    components: { ImageUpload, PageTitle, AjaxSelect, PageFooter },

    data() {
      return {
        locales: [
          'fr',
          'en'
        ],
        inventoryInitTypes: ["blank", "clone", "share"],
        countryPresets: [{
          id: 'CA',
          currencies: ['CAD'],
          regions: [
            {id: "QC", timezones: ['America/New_York']},
            {id: "ON", timezones: ['America/Toronto', 'America/Winnipeg']},
            {id: "MB", timezones: ['America/Winnipeg']},
            {id: "SK", timezones: ['America/Regina']},
            {id: "AB", timezones: ['America/Edmonton']},
            {id: "BC", timezones: ['America/Vancouver']},
            {id: "NB", timezones: ['Canada/Atlantic']},
            {id: "NS", timezones: ['Canada/Atlantic']},
            {id: "PE", timezones: ['America/New_York']},
            {id: "NL", timezones: ['America/St_Johns']},
          ],
          hourFormats: ["12", "24"]
        }, {
          id: 'US',
          currencies: ['USD'],
          timezones: [
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'US/Alaska'
          ],
          hourFormats: ["12"]
        }, {
          id: 'GB',
          currencies: ['GBP'],
          timezones: [
            'Europe/London'
          ],
          hourFormats: ["12", "24"]
        }, {
          id: 'AU',
          currencies: ['AUD'],
          timezones: [
            'Australia/Sydney',
            'Australia/Adelaide',
            'Australia/Brisbane',
            'Australia/Darwin',
            'Australia/Eucla',
            'Australia/Perth'
          ],
          hourFormats: ["12", "24"]
        }],
        augment: 'backend',
        canonical: "",
        restrictCompanySearchToBranch: true,
        company: {
          id: null,
          name: '',
          locale: null,
          country: null,
          region: null,
          currency: null,
          timezone: null,
          blockOrdersOutsideTimezone: false,
          hourFormat: null,
          mevConfig: null,
          image: {
            url: ""
          },
          contacts: [],
          remoteAccesses: [],
          inventoryInit: { /* used for creation only */
            strategy: 'blank',
            company: {id: null}, /* used when strategy = 'clone' */
            inventory: {id: null}, /* used when strategy = 'share' */
          }
        }
      }
    },

    computed: {
      localHour() {
        if (!this.company.timezone) {
          return "";
        }
        return moment().tz(this.company.timezone).format("hh:mm A (DD MMM yyyy)");
      },
      exactlyDistributor() {
        return this.$store.state.loggedUser.isDistributor;
      },
      selectedCountryPreset() {
        return this.countryPresets.find(p => p.id === this.company.country);
      },
      selectedRegionPreset() {
        let cp = this.selectedCountryPreset;
        if (cp && cp.regions && this.company.region) {
          return cp.regions.find(p => p.id === this.company.region);
        } else {
          return undefined;
        }
      },
      timezones() {
        let cp = this.selectedCountryPreset;
        let rp = this.selectedRegionPreset;
        if (rp && rp.timezones && rp.timezones.length) {
          return rp.timezones;
        } else if (cp && cp.timezones && cp.timezones.length) {
          return cp.timezones;
        } else if (cp && cp.regions && cp.regions.length) {
          return cp.regions
            .reduce((acc, r) => {
              for (let t of r.timezones || []) {
                if (!acc.includes(t)) { acc.push(t); }
                return acc;
              }
            }, []);
        } else {
          return []
        }
      },
      regions() {
        return this.selectedCountryPreset ? (this.selectedCountryPreset.regions || []) : [];
      },
      currencies() {
        return this.selectedCountryPreset ? this.selectedCountryPreset.currencies : [];
      },
      hourFormats() {
        return this.selectedCountryPreset ? this.selectedCountryPreset.hourFormats : [];
      },
      branchInventories() {
        return this.$store.state.contextBranch
          ? this.$store.state.contextBranch.inventories
          : [];
      },
      canEditRemoteAccesses() {
        return this.$store.getters.loggedUserHasDistributor;
      },
      isNew() {
        return !this.company.id;
      },
      branchId() {
        return this.$route.query.branch;
      },
      contextBranch() {
        return this.$store.state.contextBranch;
      },
      hasMultipleInventory() {
        return this.contextBranch ? !!this.contextBranch.inventories.length : false;
      }
    },

    watch: {
      'company.country'() {
        this.onCountryOrRegionChange()
      },
      'company.region'() {
        this.onCountryOrRegionChange()
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "company.presentation_title";
      this.$store.state.pageIcon = "/icon/branches.svg";
      if (this.$route.query.company) {
        await this.load(this.$route.query.branch, this.$route.query.company);
      }
    },

    methods: {

      ...mapActions(["getCompany", "saveCompany", "createCompany"]),

      onCountryOrRegionChange() {
        if (!this.regions.map(r => r.id).includes(this.company.region)) {
          this.company.region = null;
        }
        if (!this.currencies.includes(this.company.currency)) {
          this.company.currency = this.currencies[0];
        }
        if (!this.timezones.includes(this.company.timezone)) {
          this.company.timezone = this.timezones[0];
        }
        if (!this.hourFormats.includes(this.company.hourFormat)) {
          this.company.hourFormat = this.hourFormats[0];
        }
      },

      setResponse(response) {
        for (let key of Object.keys(this.company)) {
          if (response[key] !== undefined) {
            this.company[key] = response[key];
          }
        }
        if (this.company.inventory) {
          this.company.inventory = this.company.inventory.id;
        }
        if (!this.company.image) {
          this.company.image = { url: "" };
        }
        this.canonical = response.nameCanonical;
      },

      async load(branchId, companyId) {
        let response = await this.getCompany({branchId, companyId, augment: this.augment});
        if (response) {
          this.setResponse(response)
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }
        let data = JSON.parse(JSON.stringify(this.company));
        data.image = getImageData(data.image);
        if (!this.canEditRemoteAccesses) {
          data.remoteAccesses = undefined;
        }
        if (this.isNew) {
          data.inventoryInit.company = data.inventoryInit.company.id;
          data.inventoryInit.inventory = data.inventoryInit.inventory.id;
        } else {
          data.inventoryInit = undefined;
        }

        this.$store.commit("showSpinner", true);
        let response = data.id
          ? await this.saveCompany({ branchId: this.$route.query.branch, companyId: data.id, augment: this.augment, data })
          : await this.createCompany({ branchId: this.$route.query.branch, augment: this.augment, data });
        this.$store.commit("showSpinner", false);

        if (response && this.$store.state.contextCompany) {
          let inventory = this.$store.state.contextCompany.inventory;
          this.$store.state.contextCompany = response;
          this.$store.state.contextCompany.inventory = inventory;
        }

        this.setResponse(response);
        this.$store.commit('updateContextInventory', response.inventory);
        toast(this.$t("company.success"), "success");
      },

      addContact() {
        this.company.contacts.push({
          id: null,
          contactType: "",
          contactLastName: "",
          contactFirstName: "",
          companyName: "",
          phoneNumber: "",
          phoneExtension: "",
          email: "",
          notes: ""
        });
      },

      removeContact(index) {
        this.company.contacts.splice(index, 1);
      },

      addRemoteAccess() {
        this.company.remoteAccesses.push({
          id: null,
          accessTo: 'main',
          software: 'anydesk',
          username: '',
          password: '',
          notes: ''
        });
      },

      removeRemoteAccess(index) {
        this.company.remoteAccesses.splice(index, 1);
      },

      back() {
        this.$router.push(this.$router.buildContext("/companies"));
      }
    },

    validate: {
      'company.name': {
        required: true
      },
      'company.locale': {
        required: true
      },
      'company.country': {
        required: true,
      },
      'company.region': {
        required: false,
      },
      'company.currency': {
        required: true,
      },
      'company.timezone': {
        required: true,
      },
      'company.contacts': {
        array: true,
        elements: {
          contactType: {
            required: true
          },
          phoneNumber: {
            phone: true
          },
          email: {
            email: true
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  #companyEdit {
    display: flex;
    padding-bottom: 40px;
  }

  .flex-grow {
    flex-grow: 1;
  }

  .subtitle .btn {
    margin-left: 20px;
  }

  .empty {
    margin-bottom: 30px;
    color: #808080;
  }

  .contact:not(:last-child) {
    margin-bottom: 20px;
  }

  .description {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    color: #888;

    .add {
      fill: white;
      margin-left: 20px;
    }
  }

  .section-header {
    .subtitle {
      font-size: 14px;
      margin-bottom: 0;
      padding-bottom: 4px;
    }
    label {
      margin-bottom: 30px;
    }
  }
  .hr {
    width: 100%;
    border-bottom: 1px dashed #e6e6e6;
    margin: 20px 0;
  }

  .inventory-init-types {
    display: flex;
    flex-wrap: wrap;

    .inventory-init-type {
      display: flex;
      flex-direction: column;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
      border: 1px solid transparent;
      border-radius: 8px;
      padding: 16px 40px 16px 16px;
      position: relative;
      margin-right: 20px;
      margin-bottom: 20px;
      cursor: pointer;

      .svgicon {
        fill: #E6E6E6;
      }

      .inventory-init-type__name {
        font-weight: bold;
        padding-bottom: 4px;
      }
      .inventory-init-type__description {
        font-size: 14px;
        max-width: 180px;
        color: #808080;
      }
      .inventory-init-type__checkbox {
        position: absolute;
        top: 16px;
        right: 16px;
      }

      &.active {
        border-color: var(--primary-color);

        .svgicon {
          fill: var(--primary-color);
        }
      }
    }
  }
</style>